import { useState, useCallback } from "react";
import { Upload, X, Image as ImageIcon } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import { toast } from "@/hooks/use-toast";
import { recordUpload, canUpload } from "@/hooks/useUploadTracking";

const ALLOWED_TYPES = ['image/png', 'image/jpeg', 'application/pdf'];
const MAX_FILE_SIZE = 10 * 1024 * 1024; // 10MB

interface ImageUploaderProps {
  images: string[];
  onChange: (images: string[]) => void;
  maxImages?: number;
  ptaId: string;
  itemId?: string;
  bucket?: string;
  id?: string;
}

export function ImageUploader({ 
  images, 
  onChange, 
  maxImages = 3,
  ptaId,
  itemId = 'temp',
  bucket = 'shop-images',
  id = 'image-upload'
}: ImageUploaderProps) {
  const [uploading, setUploading] = useState(false);

  const handleFileChange = useCallback(async (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(e.target.files || []);
    
    if (files.length + images.length > maxImages) {
      toast({
        title: "Too many images",
        description: `Maximum ${maxImages} images allowed`,
        variant: "destructive"
      });
      return;
    }

    // Check rate limit before uploading
    const canProceed = await canUpload();
    if (!canProceed) {
      toast({
        title: "Upload limit reached",
        description: "You can only upload 30 files per 24 hours. Please try again later.",
        variant: "destructive"
      });
      return;
    }

    setUploading(true);
    const newImages: string[] = [];

    for (const file of files) {
      // Validate file type
      if (!ALLOWED_TYPES.includes(file.type)) {
        toast({
          title: "Invalid file type",
          description: "Only PNG, JPG, and PDF files are allowed",
          variant: "destructive"
        });
        continue;
      }

      // Validate file size (10MB)
      if (file.size > MAX_FILE_SIZE) {
        toast({
          title: "File too large",
          description: "Maximum file size is 10MB",
          variant: "destructive"
        });
        continue;
      }

      const timestamp = Date.now();
      const fileName = `${timestamp}-${file.name.replace(/[^a-zA-Z0-9.-]/g, '_')}`;
      const filePath = `${ptaId}/${itemId}/${fileName}`;

      const { data, error } = await supabase.storage
        .from(bucket)
        .upload(filePath, file);

      if (error) {
        // Check if it's a rate limit error from the database policy
        if (error.message?.includes('rate') || error.message?.includes('limit')) {
          toast({
            title: "Upload limit reached",
            description: "You can only upload 30 files per 24 hours. Please try again later.",
            variant: "destructive"
          });
        } else {
          toast({
            title: "Upload failed",
            description: error.message,
            variant: "destructive"
          });
        }
        continue;
      }

      // Record the upload for rate limiting
      await recordUpload(bucket, filePath);

      const { data: { publicUrl } } = supabase.storage
        .from(bucket)
        .getPublicUrl(data.path);

      newImages.push(publicUrl);
    }

    setUploading(false);
    onChange([...images, ...newImages]);

    if (newImages.length > 0) {
      toast({
        title: "Upload successful",
        description: `${newImages.length} image(s) uploaded`
      });
    }
  }, [images, maxImages, onChange, ptaId, itemId, bucket]);

  const handleRemove = useCallback((index: number) => {
    const newImages = [...images];
    newImages.splice(index, 1);
    onChange(newImages);
  }, [images, onChange]);

  const handleDrop = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    const files = Array.from(e.dataTransfer.files);
    const input = document.createElement('input');
    input.type = 'file';
    input.accept = 'image/png,image/jpeg';
    input.multiple = true;
    
    const dataTransfer = new DataTransfer();
    files.forEach(file => dataTransfer.items.add(file));
    input.files = dataTransfer.files;
    
    handleFileChange({ target: input } as any);
  }, [handleFileChange]);

  const handleDragOver = useCallback((e: React.DragEvent) => {
    e.preventDefault();
  }, []);

  return (
    <div className="space-y-4">
      {images.length > 0 && (
        <div className="grid grid-cols-3 gap-4">
          {images.map((image, index) => (
            <div key={index} className="relative group aspect-square">
              <img 
                src={image} 
                alt={`Upload ${index + 1}`} 
                className="w-full h-full object-cover rounded-lg"
              />
              <Button
                type="button"
                variant="destructive"
                size="icon"
                className="absolute top-2 right-2 opacity-0 group-hover:opacity-100 transition-opacity h-8 w-8"
                onClick={() => handleRemove(index)}
              >
                <X className="h-4 w-4" />
              </Button>
            </div>
          ))}
        </div>
      )}

      {images.length < maxImages && (
        <div
          onDrop={handleDrop}
          onDragOver={handleDragOver}
          className="border-2 border-dashed rounded-lg p-8 text-center hover:border-primary transition-colors cursor-pointer"
        >
          <input
            type="file"
            id={id}
            className="hidden"
            accept="image/png,image/jpeg,application/pdf"
            multiple
            onChange={handleFileChange}
            disabled={uploading}
          />
          <label htmlFor={id} className="cursor-pointer">
            <div className="flex flex-col items-center gap-2">
              {uploading ? (
                <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-primary" />
              ) : (
                <>
                  <ImageIcon className="h-10 w-10 text-muted-foreground" />
                  <div className="text-sm text-muted-foreground">
                    <span className="font-semibold text-primary">Click to upload</span> or drag and drop
                  </div>
                      <div className="text-xs text-muted-foreground">
                        PNG, JPG, or PDF (max 10MB each, up to {maxImages - images.length} more)
                      </div>
                </>
              )}
            </div>
          </label>
        </div>
      )}
    </div>
  );
}
